import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { EMPTY, empty, Observable, of, throwError } from 'rxjs';
import {
  catchError,
  retryWhen,
  tap,
  concatMap,
  delay,
  scan,
} from 'rxjs/operators';
import { Location } from '@angular/common';
import { Router, NavigationExtras } from '@angular/router';
import { INTERCEPTOR_SKIP } from './interceptor.constants';
import { AnalyticsService } from '../analytics/analytics.service';
import { AnalyticsData } from '../analytics/analytics.model';
import { AuthService } from '../auth/auth.service';
import { ConfigAssetLoaderService } from '../config/config-asset-loader.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private analyticsService: AnalyticsService,
    private authService: AuthService,
    private configService: ConfigAssetLoaderService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    //all apis which have 'X-Skip-Interceptor' in header are skipped for error check to avoid navigating to error page
    // if (request.headers && request.headers.has(INTERCEPTOR_SKIP)) {
    //   const headers = request.headers.delete(INTERCEPTOR_SKIP);
    //   return next.handle(request.clone({ headers }));
    // }

    const config = this.configService.getConfig();
    let ssoIssuer;
    if (config) {
      ssoIssuer = config.ssoIssuer;
    }
    const startTime = performance.now();
    return next.handle(request).pipe(
      // retryWhen operator should come before catchError operator as it is more specific
      retryWhen((errors) => this.handleRetry(errors, request, ssoIssuer)),

      // now catch all other errors
      catchError((err) => {
        //find response time of api request
        const responseTime = Math.trunc(performance.now() - startTime);
        if (err.status !== 200) {
          //log data to analytics
          const data = this.pushAnalyticsData(request, err, responseTime);
          console.log('error in api : ' + err.message);
          this.navigateOnError(err, request, data, responseTime, ssoIssuer);
        }

        return throwError(
          'Error api - ' +
            err.status +
            ' | ' +
            responseTime +
            ' | ' +
            err.message
        );
      })
    );
  }

  private navigateOnError(
    err: HttpErrorResponse,
    request: HttpRequest<any>,
    data: AnalyticsData,
    responseTime: number,
    ssoIssuer: string
  ) {
    //navigate to error page
    const navigationExtras: NavigationExtras = this.createNavigationObject(
      err,
      data,
      responseTime
    );
    if (
      !request.headers.has(INTERCEPTOR_SKIP) &&
      !request.url.startsWith(ssoIssuer)
    ) {
      if (err.status === 401) {
        //this.authService.logoutWithRedirectUrl('');
        console.log('401 error on the request url : ' + request.url);
        this.authService.loginSso(this.router.url);
        return EMPTY;
        //this.router.navigate(['401'],navigationExtras);
      } else if (err.status === 404) {
        console.log('404 error on the request url : ' + request.url);
        this.router.navigate(['404'], navigationExtras);
        return EMPTY;
      } else {
        console.log('error in main api : ' + err.status + ' | ' + request.url);
        this.router.navigate(['error'], navigationExtras);
        return EMPTY;
      }
    }
  }

  private handleRetry(
    errors: Observable<any>,
    request: HttpRequest<any>,
    ssoIssuer: string
  ): Observable<number> {
    return errors.pipe(
      scan((retryCount, error) => {
        if (
          !request.headers.has(INTERCEPTOR_SKIP) &&
          !request.url.startsWith(ssoIssuer) &&
          error.status >= 500 &&
          navigator.onLine
        ) {
          if (retryCount < 3) {
            // Check if retryCount is less than 3
            console.log(
              'retrying api: ' +
                request.url +
                ', retry count: ' +
                (retryCount + 1)
            );
            return retryCount + 1;
          } else {
            // Throw error to stop retrying after 3 attempts
            throw new Error('Retry limit reached');
          }
        } else {
          // If it doesn't meet retry criteria, throw the error immediately
          throw error;
        }
      }, 0),
      delay(2000)
    );
  }

  private createNavigationObject(
    err: any,
    data: AnalyticsData,
    responseTime: number
  ): NavigationExtras {
    return {
      skipLocationChange: true,
      state: {
        errorStatus: err.status,
        browserUrl: data.uri,
        apiUrl: data.apiUrl,
        timestamp: data.timestamp,
        token: data.token,
        responseTime: responseTime,
        UserID: data.UserID,
        transactionId: data.transactionId,
      },
    };
  }

  pushAnalyticsData(request, error, responseTime): AnalyticsData {
    return this.analyticsService.pushErrorEvent(
      request,
      error,
      responseTime,
      window.location.href
    );
  }
}
